/*
 *   Copyright (c) 2026, CYBRA Corp. All rights reserved. 
 */
import { Injectable } from "@angular/core";
import { ServiceService } from "./service.service";
import { PropertyPatch } from "app/models/property-patch";

/**
 * Constants related to the "AbleNet Return Device Validation" Service
*/
/** UUID for the "AbleNet Return Device Validation" Service Type */
export const AbleNetReturnDevicesValidation_Type_Uuid = '4b91f82b-fc5b-4b60-8bb3-bd4283ac7979';

/** Get Current State */
const FunctionKey_GetCurrentState = "get_state";
/** Function Process Return Devices */
const FunctionKey_ProcessReturnDevices = "process_return_devices";
const FunctionParameterKey_AssetEpcs = "asset_epcs";
/** Function Clear All / Cancel */
const FunctionKey_ClearAll = "clear_all";
/** Function Clear Reads */
const FunctionKey_ClearReads = "clear_reads";
/** Handle RFID Redads */
const FunctionKey_HandleRfidReads = "handle_rfid_reads";
/** Start Validation Attempt */
const FunctionKey_StartValidation = "start";
/** Stop validation Attempt */
const FunctionKey_StopValidation = "stop";



/**
 * Service which provides functions used by the "AbleNets RC Validation" process.
 * 
 * This calls Functions on "Ablenet's RC Validation" Service instances in Edgefinity and
 * returns the results.
 */
@Injectable()
export class AblenetReturnDevicesValidationService {

  constructor(
      private serviceService: ServiceService
  ) { }

    /**
     * Calls the "Get Current State" Function on the "AbleNet Shipment Validation" Service with the given ID
     * and returns the result.
     */
    public getCurrentState(portalId: number | string): Promise<ValidationInfoDto> {
      return this.serviceService.executeFunctionByKey(portalId, FunctionKey_GetCurrentState, null);
    }

        /**
     * Calls the "Get Current State" Function on the "AbleNet Shipment Validation" Service with the given ID
     * and returns the result.
     */
    public handleRfidReads(portalId: number | string): Promise<ValidationInfoDto> {
      return this.serviceService.executeFunctionByKey(portalId, FunctionKey_HandleRfidReads, null);
    }

      /**
       * Calls the "Clear Reads" Function on the "AbleNet Shipment Validation" Service with the given ID and 
       * returns the result.
       */
      public clearReads(portalId: number | string): Promise<ValidationInfoDto> {
        return this.serviceService.executeFunctionByKey(portalId, FunctionKey_ClearReads, null);
      }

      /**
       * Calls the "Clear All/Cancel" Function on the "AbleNet Shipment Validation" Service with the given ID and 
       * returns the result.
       */
      public clearAll(portalId: number | string): Promise<ValidationInfoDto> {
        return this.serviceService.executeFunctionByKey(portalId, FunctionKey_ClearAll, null);
      }

      /**
       * Calls the "Process Order" Function on the "AbleNet Shipment Validation" Service with the given ID and 
       * returns the result.
       */
      public async processReturnDevices(portalId: number | string, selectedAssetEpcs: string[]): Promise<ValidationInfoDto> {
        const functionData: PropertyPatch[] = selectedAssetEpcs.map(epc => ({
                                                                                    property: FunctionParameterKey_AssetEpcs,
                                                                                    value: epc
                                                                                  }));
        return await this.serviceService.executeFunctionByKey(portalId, FunctionKey_ProcessReturnDevices, functionData);
      }

      /**
       * Calls the "Process Order" Function on the "AbleNet Shipment Validation" Service with the given ID and 
       * returns the result.
       */
      public async start(portalId: number | string): Promise<ValidationInfoDto> {
        return await this.serviceService.executeFunctionByKey(portalId, FunctionKey_StartValidation, null);
      }

      /**
       * Calls the "Process Order" Function on the "AbleNet Shipment Validation" Service with the given ID and 
       * returns the result.
       */
      public async stop(portalId: number | string): Promise<ValidationInfoDto> {
        return await this.serviceService.executeFunctionByKey(portalId, FunctionKey_StopValidation, null);
      }

}

/**
 * Enum representing the possible "State" values for the Shipment Validation Process
 * being performed by the "AbleNet Shipment Validation" Services in Edgefinity.
 */
// export enum AbleNetReturnDevicesValidation_ValidationStateEnum {
//   /**
//    * Shipment Information needs to be entered for the Shipment / Items
//    * to be validated.
//    */
//    ReturnDeviceValidationStart = 'ReturnDeviceValidationStart',

//   /**
//    * Shipment validtion in progress..takes sometime to send request to Acumatica & get response back
//    */
//    ReturnDeviceValidationInProgress = 'ReturnDeviceValidationInProgress',

// }

/**
 * DTO to store the Asset details
 */
export interface AbleNetValidation_AssetDetailDto {
  /** EPC & asset ID value of the Tag read */
  epc: string;
  assetId: string;
  fnNumber?: string;
  orderNbr?: string;
  inventoryID?: string;
  shipmentNbr?: string;
  status?: string;
}




/**
 * DTO representing the "State" the "Shipment Validation Portal" Services
 * are currently in.  This also contains the information on any 
 * Validation Attempt being performed by the Service. 
 */
export interface ValidationInfoDto {
  //state: AbleNetReturnDevicesValidation_ValidationStateEnum;
  readerStatus: AbleNetReaderStateEnum | null;
  assets: AbleNetValidation_AssetDetailDto[];
}

/**
 * Reader Status
 */
export enum AbleNetReaderStateEnum {
    Disconnected = 'Disconnected',
    Stopped = 'Stopped',
    Started = 'Started',
    Reconnecting = 'Reconnecting...',
    Restarting = 'Restarting...'
}

